// P0-B Batch 2 · Payroll Operational Console (Six-Layer view).
//
// Per-employee vertical timeline showing every provenance layer
// between raw attendance and final paid salary:
//
//   1. Attendance Source        (attendance_events)
//   2. Attendance Reconciliation (daily_attendance_snapshots)
//   3. Leave Reconciliation     (payroll_run_attendance_freezes)
//   4. System Payroll           (engine_computed_amount)
//   5. Admin Adjustments        (payroll_run_line_adjustments · active + pending + reversed + rejected + legacy)
//   6. Final Payroll            (payroll_run_lines current amount)
//
// Reachable from:
//   • /admin/payroll/runs/:rid/employee/:eid/ops
//
// Terminology contract (tweak #2 · frozen):
//   System Calculated → Admin Adjustment → Final Payroll
//
// Pending adjustments show ONLY the Proposed Final; Layer 6 Final
// Payroll does NOT change until approval (tweak #5).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowsClockwise, Warning, Check, X, ShieldWarning, Info, ArrowSquareOut } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { toast } from "sonner";
import PLAFDrawer, { PLAFReversalDrawer } from "../../components/payroll/PLAFDrawer";
import AuthorisePaidDaysModal, { RecallAuthorisationDialog } from "../../components/payroll/AuthorisePaidDaysModal";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signed = (n) => {
    const v = Number(n || 0);
    return `${v >= 0 ? "+" : ""}${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const shortDT = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

const STATUS_STYLES = {
    active: "bg-emerald-100 text-emerald-800 border-emerald-200",
    pending_approval: "bg-amber-100 text-amber-800 border-amber-200",
    reversed: "bg-slate-200 text-slate-700 border-slate-300",
    rejected: "bg-red-100 text-red-800 border-red-200",
    legacy: "bg-blue-100 text-blue-800 border-blue-200",
};

const STATUTORY_CODES = new Set(["TDS", "PF_EE", "ESI_EE", "PT", "LWF_EE"]);


function StatusPill({ status, testId }) {
    const cls = STATUS_STYLES[status] || "bg-slate-100 text-slate-700";
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wide border ${cls}`} data-testid={testId}>
            {String(status || "").replace("_", " ")}
        </span>
    );
}


function SummaryStrip({ view, adjustments, employee }) {
    const line = view.layers?.final_payroll || {};
    const systemGross = view.layers?.system_payroll?.gross_earnings || 0;
    const systemDed = view.layers?.system_payroll?.total_deductions || 0;
    const systemNet = systemGross - systemDed;
    const finalGross = line.gross_earnings || 0;
    const finalDed = line.total_deductions || 0;
    const finalNet = line.net_pay || 0;
    const totalAdjustment = finalNet - systemNet;
    const attSys = view.layers?.system_payroll?.attendance_summary || {};
    const attFinal = view.layers?.final_payroll?.attendance_summary || {};
    // Exclude reversal rows from the "Active" chip — reversal rows keep
    // status="active" by design (append-only audit trail) but represent
    // historical reversal events, not live adjustments. iter164 UX nit.
    const active = (adjustments || []).filter((a) => a.status === "active" && !a.legacy && a.mode !== "reversal");
    const pending = (adjustments || []).filter((a) => a.status === "pending_approval");
    const rejected = (adjustments || []).filter((a) => a.status === "rejected");
    let adjustmentStatus = "None";
    if (pending.length) adjustmentStatus = `Pending Approval (${pending.length})`;
    else if (active.length) adjustmentStatus = `Adjusted (${active.length})`;
    else if (rejected.length) adjustmentStatus = `Rejected (${rejected.length})`;
    return (
        <section className="border rounded-xl bg-white p-4 mb-4" data-testid="ops-summary-strip">
            <div className="flex items-baseline justify-between mb-3">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Employee</div>
                    <div className="text-lg font-semibold" data-testid="ops-summary-emp-name">
                        {employee?.full_name || "—"} <span className="text-xs text-slate-500 font-mono ml-2">{employee?.employee_code || ""}</span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Payroll Period</div>
                    <div className="text-sm font-mono" data-testid="ops-summary-period">
                        {view.run?.period_start} → {view.run?.period_end}
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2 text-xs">
                <div>
                    <div className="uppercase text-[10px] tracking-wider text-slate-500">System Gross</div>
                    <div className="font-mono" data-testid="ops-summary-system-gross">{inr(systemGross)}</div>
                </div>
                <div>
                    <div className="uppercase text-[10px] tracking-wider text-slate-500">System Deductions</div>
                    <div className="font-mono">{inr(systemDed)}</div>
                </div>
                <div>
                    <div className="uppercase text-[10px] tracking-wider text-slate-500">System Net Pay</div>
                    <div className="font-mono" data-testid="ops-summary-system-net">{inr(systemNet)}</div>
                </div>
                <div>
                    <div className="uppercase text-[10px] tracking-wider text-amber-700">Total Admin Adjustment</div>
                    <div className={`font-mono ${totalAdjustment !== 0 ? "text-amber-900 font-semibold" : ""}`} data-testid="ops-summary-total-adjustment">
                        {signed(totalAdjustment)}
                    </div>
                </div>
                <div>
                    <div className="uppercase text-[10px] tracking-wider text-emerald-700">Final Gross</div>
                    <div className="font-mono font-semibold text-emerald-900">{inr(finalGross)}</div>
                </div>
                <div>
                    <div className="uppercase text-[10px] tracking-wider text-emerald-700">Final Deductions</div>
                    <div className="font-mono font-semibold text-emerald-900">{inr(finalDed)}</div>
                </div>
                <div>
                    <div className="uppercase text-[10px] tracking-wider text-emerald-700">Final Net Pay</div>
                    <div className="font-mono text-base font-bold text-emerald-900" data-testid="ops-summary-final-net">{inr(finalNet)}</div>
                </div>
                <div>
                    <div className="uppercase text-[10px] tracking-wider text-slate-500">LOP · System → Final</div>
                    <div className="font-mono" data-testid="ops-summary-lop">
                        {Number(attSys.lop_days || 0).toFixed(2)} → <span className="font-semibold">{Number(attFinal.lop_days || 0).toFixed(2)}</span>
                    </div>
                </div>
            </div>
            <div className="mt-3 pt-3 border-t flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wider text-slate-500">Adjustment Status</span>
                <Badge
                    className={pending.length ? "bg-amber-100 text-amber-800" : active.length ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}
                    data-testid="ops-summary-status"
                >
                    {adjustmentStatus}
                </Badge>
            </div>
        </section>
    );
}


function FreshnessBanner({ freshness, onRefresh }) {
    if (!freshness || !freshness.stale) return null;
    return (
        <Alert className="mb-4 bg-amber-50 border-amber-300 text-amber-900" data-testid="ops-freshness-banner">
            <Warning size={16} className="inline mr-1" />
            <AlertTitle className="text-amber-900">Source data has changed since this payroll line was computed</AlertTitle>
            <AlertDescription className="text-amber-900">
                <ul className="text-xs space-y-1 mt-1">
                    {(freshness.sources || []).map((s, i) => (
                        <li key={i} data-testid={`ops-freshness-source-${s.source}`}>
                            <strong>{s.label}</strong> changed at {shortDT(s.last_change_at)} · <em>{s.action}</em>
                        </li>
                    ))}
                </ul>
                <div className="mt-2 text-xs">Re-Freeze or Recalculate the payroll run to reflect these changes in Final Payroll.</div>
                <Button size="sm" variant="outline" className="mt-2" onClick={onRefresh} data-testid="ops-freshness-refresh">
                    <ArrowsClockwise size={12} className="mr-1" />Recheck
                </Button>
            </AlertDescription>
        </Alert>
    );
}


function Layer1AttendanceSource({ events, sources, rid, eid }) {
    return (
        <div className="space-y-2">
            <div className="flex items-baseline justify-between">
                <div className="text-xs text-slate-600">
                    Sources present: {sources?.length ? sources.map((s) => <Badge key={s} className="bg-slate-100 text-slate-700 mr-1">{s}</Badge>) : "—"}
                </div>
                <Link to={`/attendance/monthly-register?employee_id=${eid}&return_to=/admin/payroll/runs/${rid}/employee/${eid}/ops`}
                    className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                    data-testid="ops-open-in-attendance"
                >
                    <ArrowSquareOut size={12} />Open in Attendance
                </Link>
            </div>
            {events?.length === 0 ? (
                <div className="text-xs text-slate-500 italic">No attendance events for this period.</div>
            ) : (
                <div className="max-h-64 overflow-auto border rounded">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0"><tr>
                            <th className="px-2 py-1 text-left">Time</th>
                            <th className="px-2 py-1 text-left">Type</th>
                            <th className="px-2 py-1 text-left">Source</th>
                            <th className="px-2 py-1 text-left">Note</th>
                        </tr></thead>
                        <tbody>
                            {(events || []).slice(0, 100).map((e, i) => (
                                <tr key={i} className="border-t">
                                    <td className="px-2 py-1 font-mono">{shortDT(e.event_time)}</td>
                                    <td className="px-2 py-1">{e.event_type || "—"}</td>
                                    <td className="px-2 py-1"><Badge className="bg-slate-100">{e.source || "—"}</Badge></td>
                                    <td className="px-2 py-1 text-slate-500">{e.note || ""}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


function Layer2Reconciled({ snapshots, rid, eid }) {
    return (
        <div>
            <div className="flex justify-end mb-2">
                <Link to={`/attendance/regularize?employee_id=${eid}&return_to=/admin/payroll/runs/${rid}/employee/${eid}/ops`}
                    className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                    data-testid="ops-regularise-link"
                ><ArrowSquareOut size={12} />Regularise in Attendance</Link>
            </div>
            <div className="max-h-64 overflow-auto border rounded">
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0"><tr>
                        <th className="px-2 py-1 text-left">Date</th>
                        <th className="px-2 py-1 text-left">Status</th>
                        <th className="px-2 py-1 text-right">Working</th>
                        <th className="px-2 py-1 text-left">Note</th>
                    </tr></thead>
                    <tbody>
                        {(snapshots || []).map((s, i) => (
                            <tr key={i} className="border-t">
                                <td className="px-2 py-1 font-mono">{s.attendance_date}</td>
                                <td className="px-2 py-1">{s.attendance_status || "—"}</td>
                                <td className="px-2 py-1 text-right font-mono">{Number(s.working_units || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-slate-500">{s.notes || ""}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function Layer3LeaveRecon({ leaveRecon, eid }) {
    if (!leaveRecon) {
        return <div className="text-xs text-slate-500 italic">No leave reconciliation snapshot yet — payroll not frozen for this employee.</div>;
    }
    const per_request = leaveRecon.per_request || [];
    const per_type = leaveRecon.per_type || {};
    return (
        <div className="space-y-3">
            <div className="flex items-baseline justify-between">
                <div className="text-xs text-slate-600">Authoritative freeze-time split · <span className="italic">source ledger untouched by PLAF</span></div>
                <Link to={`/leave/manager?employee_id=${eid}`}
                    className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                    data-testid="ops-adjust-leave-link"
                ><ArrowSquareOut size={12} />Adjust Leave Request</Link>
            </div>
            {Object.keys(per_type).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(per_type).map(([code, block]) => (
                        <div key={code} className="border rounded p-2 text-xs">
                            <div className="uppercase text-[10px] tracking-wider text-slate-500">{code}</div>
                            <div className="font-mono mt-1">Balance @ freeze: {Number(block.applicable_balance || 0).toFixed(2)}</div>
                            <div className="font-mono text-emerald-800">Paid: {Number(block.credited_paid_days || 0).toFixed(2)}</div>
                            <div className="font-mono text-red-800">LOP: {Number(block.debited_lop_days || 0).toFixed(2)}</div>
                        </div>
                    ))}
                </div>
            )}
            {per_request.length > 0 && (
                <div className="border rounded max-h-64 overflow-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0"><tr>
                            <th className="px-2 py-1 text-left">Request</th>
                            <th className="px-2 py-1 text-left">Type</th>
                            <th className="px-2 py-1 text-right">Requested</th>
                            <th className="px-2 py-1 text-right">Paid</th>
                            <th className="px-2 py-1 text-right">LOP</th>
                            <th className="px-2 py-1 text-left">Ledger IDs</th>
                        </tr></thead>
                        <tbody>
                            {per_request.map((r, i) => (
                                <tr key={i} className="border-t">
                                    <td className="px-2 py-1 font-mono">{r.request_id || "—"}</td>
                                    <td className="px-2 py-1">{r.leave_type_code || "—"}</td>
                                    <td className="px-2 py-1 text-right font-mono">{Number(r.days_total || 0).toFixed(2)}</td>
                                    <td className="px-2 py-1 text-right font-mono text-emerald-800">{Number(r.reconciled_paid_days || 0).toFixed(2)}</td>
                                    <td className="px-2 py-1 text-right font-mono text-red-800">{Number(r.reconciled_lop_days || 0).toFixed(2)}</td>
                                    <td className="px-2 py-1 text-[10px] text-slate-500">{(r.ledger_ids || []).join(", ")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


function ComponentRow({ label, systemAmt, adjustment, finalAmt, statutory }) {
    const delta = Number(finalAmt || 0) - Number(systemAmt || 0);
    return (
        <tr className="border-t hover:bg-slate-50">
            <td className="px-2 py-1 font-mono text-[11px]">
                {label}
                {statutory && (
                    <Badge className="ml-1 bg-red-100 text-red-800 border border-red-200 text-[9px] px-1 py-0"><ShieldWarning size={10} className="inline" /></Badge>
                )}
            </td>
            <td className="px-2 py-1 text-right font-mono">{inr(systemAmt)}</td>
            <td className={`px-2 py-1 text-right font-mono ${delta !== 0 ? "text-amber-900 font-semibold" : "text-slate-400"}`}>{delta !== 0 ? signed(delta) : "—"}</td>
            <td className={`px-2 py-1 text-right font-mono ${delta !== 0 ? "text-emerald-900 font-semibold" : ""}`}>{inr(finalAmt)}</td>
        </tr>
    );
}


function Layer4And6PayrollTable({ line, layerType }) {
    if (!line) return <div className="text-xs text-slate-500 italic">Payroll not calculated yet.</div>;
    const isSystem = layerType === "system";
    const earnings = line.earnings || [];
    const deductions = line.deductions || [];
    // For system view show only engine_computed_amount, for final view show amount + delta
    return (
        <div className="border rounded max-h-96 overflow-auto">
            <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                    <tr>
                        <th className="px-2 py-1 text-left">Component</th>
                        <th className="px-2 py-1 text-right">System Calculated</th>
                        {!isSystem && <th className="px-2 py-1 text-right">Admin Adjustment</th>}
                        {!isSystem && <th className="px-2 py-1 text-right">Final Payroll</th>}
                    </tr>
                </thead>
                <tbody>
                    <tr className="bg-slate-100"><td colSpan={isSystem ? 2 : 4} className="px-2 py-1 uppercase text-[10px] tracking-wider text-slate-500">Earnings</td></tr>
                    {earnings.map((r, i) => (
                        <ComponentRow
                            key={`e-${i}`}
                            label={r.component_code}
                            systemAmt={r.engine_computed_amount ?? r.amount}
                            adjustment={r.admin_adjustment}
                            finalAmt={r.amount}
                            statutory={STATUTORY_CODES.has(r.component_code)}
                        />
                    ))}
                    <tr className="bg-slate-100"><td colSpan={isSystem ? 2 : 4} className="px-2 py-1 uppercase text-[10px] tracking-wider text-slate-500">Deductions</td></tr>
                    {deductions.map((r, i) => (
                        <ComponentRow
                            key={`d-${i}`}
                            label={r.component_code}
                            systemAmt={r.engine_computed_amount ?? r.amount}
                            adjustment={r.admin_adjustment}
                            finalAmt={r.amount}
                            statutory={STATUTORY_CODES.has(r.component_code)}
                        />
                    ))}
                    <tr className="bg-slate-50 border-t-2 border-slate-300">
                        <td className="px-2 py-1 font-semibold">Net Pay</td>
                        <td className="px-2 py-1 text-right font-mono font-semibold">
                            {inr((line.earnings || []).reduce((s, r) => s + Number(r.engine_computed_amount ?? r.amount ?? 0), 0)
                              - (line.deductions || []).reduce((s, r) => s + Number(r.engine_computed_amount ?? r.amount ?? 0), 0))}
                        </td>
                        {!isSystem && <td className="px-2 py-1"></td>}
                        {!isSystem && <td className="px-2 py-1 text-right font-mono font-bold text-emerald-900">{inr(line.net_pay)}</td>}
                    </tr>
                </tbody>
            </table>
        </div>
    );
}


function Layer5Adjustments({ adjustments, onAddAdjustment, onReverseAdjustment, onChanged, runStatus }) {
    const [recallT, setRecallT] = useState(null);
    const [reviseT, setReviseT] = useState(null);
    // Editable while the run is Draft / Calculated / Pending Approval —
    // attendance freeze never gates this; once the run stage locks
    // changes (Approved+), operators use the correction-run workflow.
    const runEditable = !runStatus || ["Draft", "Calculated", "Pending Approval"].includes(runStatus);
    const groups = {
        pending_approval: [], active: [], reversals: [], reversed: [], rejected: [], legacy: [],
    };
    for (const a of adjustments || []) {
        if (a.legacy) groups.legacy.push(a);
        else if (a.mode === "reversal") groups.reversals.push(a);
        else groups[a.status]?.push(a);
    }

    const renderGroup = (label, rows, extraCol = null) => rows.length > 0 && (
        <div className="mb-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label} <span className="text-slate-400">({rows.length})</span></div>
            <div className="border rounded overflow-hidden">
                <table className="w-full text-xs">
                    <thead className="bg-slate-50"><tr>
                        <th className="px-2 py-1 text-left">Component</th>
                        <th className="px-2 py-1 text-left">Mode</th>
                        <th className="px-2 py-1 text-right">System</th>
                        <th className="px-2 py-1 text-right">Δ Admin</th>
                        <th className="px-2 py-1 text-right">{label === "Pending Approval" ? "Proposed Final" : "Final"}</th>
                        <th className="px-2 py-1 text-left">Status</th>
                        <th className="px-2 py-1 text-left">By</th>
                        <th className="px-2 py-1 text-left">Reason</th>
                        <th className="px-2 py-1 text-left">AAS</th>
                        {extraCol && <th className="px-2 py-1"></th>}
                    </tr></thead>
                    <tbody>
                        {rows.map((a) => {
                            const sc = a.target?.component_code;
                            const statutory = STATUTORY_CODES.has(sc);
                            const sys = typeof a.system_value === "object" ? "(struct)" : inr(a.system_value);
                            const adm = typeof a.admin_adjustment === "object" ? "(struct)" : signed(a.admin_adjustment);
                            const fin = typeof a.final_value === "object" ? "(struct)" : inr(a.final_value);
                            return (
                                <tr key={a.id} className="border-t" data-testid={`plaf-row-${a.id}`}>
                                    <td className="px-2 py-1 font-mono">
                                        {sc}
                                        {statutory && <Badge className="ml-1 bg-red-100 text-red-800 text-[9px] px-1 py-0"><ShieldWarning size={9} className="inline" /></Badge>}
                                    </td>
                                    <td className="px-2 py-1">{a.mode}</td>
                                    <td className="px-2 py-1 text-right font-mono">{sys}</td>
                                    <td className="px-2 py-1 text-right font-mono text-amber-900">{adm}</td>
                                    <td className="px-2 py-1 text-right font-mono font-semibold">{fin}</td>
                                    <td className="px-2 py-1"><StatusPill status={a.legacy ? "legacy" : a.status} /></td>
                                    <td className="px-2 py-1 text-[10px]">{a.created_by_email || "—"}</td>
                                    <td className="px-2 py-1 text-[10px] max-w-[200px] truncate" title={a.reason}>{a.reason || "—"}</td>
                                    <td className="px-2 py-1 font-mono text-[10px] text-slate-500">{a.aas_action_id || "—"}</td>
                                    {extraCol && (
                                        <td className="px-2 py-1">
                                            {a.status === "active" && !a.legacy && (
                                                sc === "ATTENDANCE_SUMMARY" ? (
                                                    <div className="flex gap-1">
                                                        <Button size="sm" variant="outline"
                                                            disabled={!runEditable}
                                                            title={runEditable ? "Replace this authorisation (reverses it first — no stacking)" : `Run is ${runStatus} — use the Correction Run workflow`}
                                                            onClick={() => runEditable && setReviseT(a)}
                                                            data-testid={`plaf-revise-btn-${a.id}`}>Revise</Button>
                                                        <Button size="sm" variant="outline"
                                                            disabled={!runEditable}
                                                            title={runEditable ? "Recall (reverse) this authorisation through the audit trail, then recalculate" : `Run is ${runStatus} — use the Correction Run workflow`}
                                                            onClick={() => runEditable && setRecallT(a)}
                                                            data-testid={`plaf-recall-btn-${a.id}`}>Recall</Button>
                                                    </div>
                                                ) : (
                                                    <Button size="sm" variant="outline" onClick={() => onReverseAdjustment(a)} data-testid={`plaf-reverse-btn-${a.id}`}>Reverse</Button>
                                                )
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="space-y-3">
            <div className="flex justify-between items-center">
                <div className="text-xs text-slate-600">
                    Adjustments are appended to <span className="font-mono">payroll_run_line_adjustments</span> — source ledgers (Leave / Loan / Tax) are untouched.
                </div>
                <Button size="sm" onClick={onAddAdjustment} data-testid="plaf-add-adjustment-btn">Add Adjustment</Button>
            </div>
            {(adjustments || []).length === 0 ? (
                <div className="text-xs text-slate-500 italic">No adjustments recorded on this employee for this run.</div>
            ) : (
                <>
                    {renderGroup("Pending Approval", groups.pending_approval, true)}
                    {renderGroup("Active", groups.active, true)}
                    {renderGroup("Reversals (audit history)", groups.reversals, false)}
                    {renderGroup("Reversed", groups.reversed, false)}
                    {renderGroup("Rejected", groups.rejected, false)}
                    {renderGroup("Legacy Overrides", groups.legacy, false)}
                </>
            )}
            {recallT && (
                <RecallAuthorisationDialog
                    open={!!recallT}
                    onClose={() => setRecallT(null)}
                    runId={recallT.payroll_run_id}
                    employeeId={recallT.employee_id}
                    adjustment={recallT}
                    onCompleted={() => onChanged?.()}
                />
            )}
            {reviseT && (
                <AuthorisePaidDaysModal
                    open={!!reviseT}
                    onClose={() => setReviseT(null)}
                    runId={reviseT.payroll_run_id}
                    employeeId={reviseT.employee_id}
                    reviseOf={reviseT}
                    onCompleted={() => onChanged?.()}
                />
            )}
        </div>
    );
}


export default function PayrollOpsSixLayer() {
    const { rid, eid } = useParams();
    const nav = useNavigate();
    const [view, setView] = useState(null);
    const [history, setHistory] = useState([]);
    const [freshness, setFreshness] = useState(null);
    const [line, setLine] = useState(null);
    const [policies, setPolicies] = useState([]);
    const [emp, setEmp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [prefillCode, setPrefillCode] = useState(null);
    const [revTarget, setRevTarget] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const [vw, hi, fresh, pol, empRes] = await Promise.all([
                api.get(`/payroll/run/${rid}/employee/${eid}/six-layer-view`),
                api.get(`/payroll/run/${rid}/employee/${eid}/adjustments/history`),
                api.get(`/payroll/run/${rid}/employee/${eid}/source-freshness`),
                api.get(`/payroll/component-policies`),
                api.get(`/hr/employee/${eid}`).catch(() => ({ data: null })),
            ]);
            setView(vw.data);
            setLine(vw.data?.layers?.final_payroll ? {
                ...vw.data.layers.final_payroll,
                earnings: vw.data.__line_earnings || undefined,
                deductions: vw.data.__line_deductions || undefined,
            } : null);
            setHistory([...(hi.data.history || [])]);
            setFreshness(fresh.data);
            setPolicies(pol.data?.items || []);
            setEmp(empRes.data || null);
            // We need the full line for the drawer's Net-pay preview.
            // Six-layer-view only returns summaries; fetch the raw line
            // separately.
            const lineFull = await api.get(`/payroll/run/${rid}/lines/${eid}`).catch(() => null);
            if (lineFull?.data) setLine(lineFull.data);
        } catch (ex) {
            setErr(formatApiError(ex));
        } finally {
            setLoading(false);
        }
    }, [rid, eid]);

    useEffect(() => { load(); }, [load]);

    // Reversal rows carry status="active" by design (audit trail) but are
    // not live adjustments — exclude them from the "Active" indicator so
    // the chip count matches the renderable action-bearing rows below.
    const active = useMemo(() => (history || []).filter((h) => h.status === "active" && !h.legacy && h.mode !== "reversal"), [history]);
    const pending = useMemo(() => (history || []).filter((h) => h.status === "pending_approval"), [history]);

    if (loading && !view) return <div className="p-6 text-sm text-slate-500">Loading operational console…</div>;
    if (err) return <div className="p-6"><Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert></div>;
    if (!view) return <div className="p-6 text-sm text-slate-500">No payroll data for this employee on this run.</div>;

    const layers = view.layers || {};

    return (
        <div className="p-4 max-w-6xl mx-auto" data-testid="payroll-ops-console">
            <PageHeader
                title="Payroll Operational Console"
                subtitle={`Six-layer view · Run ${rid} · Employee ${eid}`}
                actions={(
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={load} data-testid="ops-refresh"><ArrowsClockwise size={14} className="mr-1" />Refresh</Button>
                        <Link to={`/admin/payroll/runs/${rid}`}><Button size="sm" variant="outline"><ArrowLeft size={14} className="mr-1" />Back to run</Button></Link>
                    </div>
                )}
            />

            <FreshnessBanner freshness={freshness} onRefresh={load} />

            <SummaryStrip view={view} adjustments={history} employee={emp} />

            <Accordion type="multiple" defaultValue={["layer-5", "layer-6"]} data-testid="ops-layers">
                <AccordionItem value="layer-1">
                    <AccordionTrigger data-testid="ops-layer-1">
                        <div className="flex items-baseline gap-2">
                            <Badge className="bg-slate-200 text-slate-800">1</Badge>
                            <span className="font-semibold">Attendance Source</span>
                            <span className="text-xs text-slate-500">· {layers.attendance_source?.events?.length || 0} events</span>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <Layer1AttendanceSource
                            events={layers.attendance_source?.events || []}
                            sources={layers.attendance_source?.sources_present || []}
                            rid={rid} eid={eid}
                        />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="layer-2">
                    <AccordionTrigger data-testid="ops-layer-2">
                        <div className="flex items-baseline gap-2">
                            <Badge className="bg-slate-200 text-slate-800">2</Badge>
                            <span className="font-semibold">Attendance Reconciliation</span>
                            <span className="text-xs text-slate-500">· {layers.reconciled_attendance?.snapshots?.length || 0} days</span>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <Layer2Reconciled snapshots={layers.reconciled_attendance?.snapshots || []} rid={rid} eid={eid} />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="layer-3">
                    <AccordionTrigger data-testid="ops-layer-3">
                        <div className="flex items-baseline gap-2">
                            <Badge className="bg-slate-200 text-slate-800">3</Badge>
                            <span className="font-semibold">Leave Reconciliation</span>
                            <span className="text-xs text-slate-500">· authoritative freeze-time split</span>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <Layer3LeaveRecon leaveRecon={layers.leave_reconciliation} eid={eid} />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="layer-4">
                    <AccordionTrigger data-testid="ops-layer-4">
                        <div className="flex items-baseline gap-2">
                            <Badge className="bg-slate-200 text-slate-800">4</Badge>
                            <span className="font-semibold">System Payroll <span className="text-[10px] font-normal text-slate-500">(engine baseline)</span></span>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <Layer4And6PayrollTable line={line} layerType="system" />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="layer-5">
                    <AccordionTrigger data-testid="ops-layer-5">
                        <div className="flex items-baseline gap-2">
                            <Badge className="bg-slate-200 text-slate-800">5</Badge>
                            <span className="font-semibold">Admin Adjustments</span>
                            {pending.length > 0 && <Badge className="bg-amber-100 text-amber-800">{pending.length} Pending</Badge>}
                            {active.length > 0 && <Badge className="bg-emerald-100 text-emerald-800">{active.length} Active</Badge>}
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <Layer5Adjustments
                            adjustments={history}
                            onAddAdjustment={() => { setPrefillCode(null); setAddOpen(true); }}
                            onReverseAdjustment={(a) => setRevTarget(a)}
                            onChanged={load}
                            runStatus={view.run?.status}
                        />
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="layer-6">
                    <AccordionTrigger data-testid="ops-layer-6">
                        <div className="flex items-baseline gap-2">
                            <Badge className="bg-emerald-200 text-emerald-900">6</Badge>
                            <span className="font-semibold text-emerald-900">Final Payroll</span>
                            <span className="text-xs text-emerald-700 font-mono">Net {inr(layers.final_payroll?.net_pay)}</span>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <div className="text-[11px] text-slate-500 mb-2 italic">
                            Pending adjustments do NOT change these figures — only active adjustments are reflected in Final Payroll.
                        </div>
                        <Layer4And6PayrollTable line={line} layerType="final" />
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            <PLAFDrawer
                open={addOpen}
                onClose={() => setAddOpen(false)}
                runId={rid}
                employeeId={eid}
                prefillComponentCode={prefillCode}
                line={line}
                policies={policies}
                onSubmitted={() => { load(); }}
            />
            {revTarget && (
                <PLAFReversalDrawer
                    open={!!revTarget}
                    onClose={() => setRevTarget(null)}
                    runId={rid}
                    employeeId={eid}
                    adjustmentId={revTarget.id}
                    componentCode={revTarget.target?.component_code}
                    onSubmitted={() => { load(); }}
                />
            )}
        </div>
    );
}
